/**
 * AppNotificationRemapper
 *
 * Bridges Firestore wire JSON ↔ `AppNotificationDto` ↔ `AppNotificationEntity`,
 * following the `fromJson(): Dto` + `fromDto(): Entity` convention codified by
 * `chatMessageRemapper.ts` and `data-layer.md`.
 * Flutter source: lib/data/models/app_notification.dart
 *   - `id`    ← `data['id']`
 *   - `info`  ← `NotificationInfoRemapper` (`fromJson` → DTO, `fromDto` → entity)
 *   - `stats` ← `NotificationStatsRemapper` (`fromJson` → DTO, `fromDto` → entity)
 *     when present
 *
 * `info`/`stats` are mapped through their dedicated sub-remappers (never copied
 * field-by-field here) so the shapes can never drift from `NotificationInfoDto` /
 * `NotificationStatsDto` (and their entities).
 */
import type { AppNotificationDto } from '@/data/dtos/notification/appNotificationDto';
import type { AppNotificationEntity } from '@/domain/entities/notification/appNotificationEntity';
import type { JsonObject } from '@/data/types/json';
import { NotificationStatsRemapper } from '@/domain/remappers/info/notificationStatsRemapper';
import { NotificationInfoRemapper } from '@/domain/remappers/notificationInfo/notificationInfoRemapper';

export class AppNotificationRemapper {
  /** Parse a Firestore `notifications` document into an `AppNotificationDto`. */
  static fromJson(data: JsonObject): AppNotificationDto {
    const info = (data['info'] as JsonObject | undefined) ?? {};
    const stats = data['stats'] as JsonObject | undefined;
    return {
      id: data['id'] as string | undefined,
      info: NotificationInfoRemapper.fromJson(info),
      stats: stats ? NotificationStatsRemapper.fromJson(stats) : undefined,
    };
  }

  static fromJsonList(dataList: JsonObject[]): AppNotificationDto[] {
    return dataList.map(AppNotificationRemapper.fromJson);
  }

  /** Build an `AppNotificationEntity` from an `AppNotificationDto`. */
  static fromDto(dto: AppNotificationDto): AppNotificationEntity {
    return {
      id: dto.id,
      info: NotificationInfoRemapper.fromDto(dto.info),
      stats: dto.stats ? NotificationStatsRemapper.fromDto(dto.stats) : undefined,
    };
  }

  static fromDtoList(dtoList: AppNotificationDto[]): AppNotificationEntity[] {
    return dtoList.map(AppNotificationRemapper.fromDto);
  }

  /**
   * Build an `AppNotificationDto` from an `AppNotificationEntity` — the reverse
   * of `fromDto`. `info` is routed through `NotificationInfoRemapper` (never
   * copied field-by-field here); `stats` is field-identical DTO↔entity so it
   * carries through as-is (a live notification never sets it).
   */
  static toDtoFromEntity(entity: AppNotificationEntity): AppNotificationDto {
    return {
      id: entity.id,
      info: NotificationInfoRemapper.toDtoFromEntity(entity.info),
      stats: entity.stats,
    };
  }

  /**
   * Serialize an `AppNotificationDto` to its Firestore wire map. Parity with
   * Flutter's `_$AppNotificationToJson` (lib/data/models/app_notification.g.dart:18-23)
   * via the nested `info.toJson()`. `stats` is omitted (a live notification never
   * sets it; a future stats-writer adds a `NotificationStatsRemapper.toJson`
   * emission then). `id` is emitted only when set.
   */
  static toJson(dto: AppNotificationDto): JsonObject {
    return {
      ...(dto.id !== undefined ? { id: dto.id } : {}),
      info: NotificationInfoRemapper.toJson(dto.info),
    };
  }
}
